import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CapaEffectivenessReferenceDocument from './components/CapaEffectivenessReferenceDocument';
import CapaEffectivenessVerificationMethod from './components/CapaEffectivenessVerificationMethod';
import CapaEffectivenessEvidenceReviewed from './components/CapaEffectivenessEvidenceReviewed';
import CustomCapaEffectivenessSplitView from './CustomCapaEffectivenessSplitView';

const trackingLabels = getLabels('CAPA Effectiveness Check');
const trackingBase = createTrackingConfig('CAPA Effectiveness Check');

const EFFECTIVENESS_RESULT_OPTIONS = [
  { value: 'EFFECTIVE', label: 'Effective' },
  { value: 'PARTIALLY_EFFECTIVE', label: 'Partially effective' },
  { value: 'NOT_EFFECTIVE', label: 'Not effective' },
];

const DECISION_OPTIONS = [
  { value: 'CLOSE_CAPA', label: 'Close CAPA' },
  { value: 'KEEP_OPEN', label: 'Keep CAPA open' },
];

/** Show closure fields only when selected status is a closed status. */
const isClosedStatusSelected = (formData, lookupData) => {
  const statusId = formData?.statusId;
  if (statusId == null || statusId === '') return false;

  const statuses =
    lookupData?.capaEffectivenessCheckStatuses?.data ||
    lookupData?.capaEffectivenessCheckStatuses ||
    [];
  const list = Array.isArray(statuses) ? statuses : [];
  const match = list.find((s) => Number(s.id) === Number(statusId));
  if (match) {
    return match.isClosedStatus === true;
  }

  if (formData?.status && Number(formData.status.id) === Number(statusId)) {
    return formData.status.isClosedStatus === true;
  }
  return false;
};

export const capaEffectivenessConfig = {
  entityName: 'CapaEffectivenessCheck',
  entityTypeId: 20,

  api: {
    baseEndpoint: '/capa-effectiveness-check',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
    },
    lookupEndpoints: {
      users: '/users',
      capaEffectivenessCheckStatuses: '/capa-effectiveness-check-status',
    },
    swrInvalidation: {
      mainList: '/capa-effectiveness-check',
      fileAttachments: (entityTypeId) =>
        `/file-attachment/entity/${entityTypeId}/*`,
    },
  },

  ui: {
    drawerWidth: '50%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
  },

  customSplitViewPanel: {
    enabled: true,
    component: CustomCapaEffectivenessSplitView,
  },

  features: {
    fileAttachments: true,
    masterEntityTypeId: 20,
    approvalRequests: false,
  },

  displayName: 'CAPA Effectiveness Check',
  description:
    'Documentation of CAPA effectiveness verification (ΠΑΡ.2-ΓΔ.13)',
  icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',

  fieldsToRemove: [
    'id',
    'status',
    'checkedBy',
    'approvedBy',
    'closedBy',
    'createdBy',
    'updatedBy',
    'createdById',
    'updatedById',
    'createdAt',
    'updatedAt',
    'fileAttachments',
    'fileAttachmentCount',
  ],
  fieldsToKeep: [],

  fields: [
    { name: 'id', label: 'ID', type: 'hidden', table: false, filterable: false },
    {
      name: 'code',
      label: 'CAPA Number',
      type: 'text',
      required: true,
      placeholder: 'e.g. CAPA-2026-001',
      table: { header: 'CAPA No.', width: '140px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 100,
        message: 'CAPA number is required',
      },
    },
    {
      name: 'relatedDeviationOrFinding',
      label: 'Related Deviation / Inspection Finding',
      type: 'text',
      required: true,
      placeholder: 'Related deviation or inspection finding',
      table: {
        header: 'Related Deviation/Finding',
        width: '200px',
        sortable: true,
      },
      filterable: true,
      validation: {
        required: true,
        maxLength: 500,
        message: 'Related Deviation / Inspection Finding is required',
      },
    },
    {
      name: 'capaDescription',
      label: 'CAPA Description',
      type: 'textarea',
      required: true,
      placeholder: 'Describe the corrective and preventive action',
      rows: 4,
      table: {
        header: 'CAPA Description',
        width: '220px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
      validation: {
        required: true,
        message: 'CAPA Description is required',
      },
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'capaEffectivenessCheckStatuses',
      options: [],
      table: {
        header: 'Status',
        width: '120px',
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { required: true, integer: true },
    },
    {
      name: 'referenceDocument',
      label: 'Reference Document',
      type: 'hidden',
      required: false,
      table: {
        header: 'Reference Document',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
    },

    // Verification methods (read-only boilerplate; stored as true on create)
    {
      name: 'methodReviewFiles',
      label: 'Review of related files and documents',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'methodVerifyRevisedProcedures',
      label: 'Verification of revised procedures implementation',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'methodEffectivenessControlsDocumented',
      label: 'Effectiveness controls are documented',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'methodDueDateDefined',
      label: 'Due date defined for effectiveness check / CAPA closure',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'methodStaffInformed',
      label: 'Involved staff informed of new requirements',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'methodQmsReview',
      label: 'Review of QMS records',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },

    // Criteria
    {
      name: 'criteriaCapaFullyImplemented',
      label: 'CAPA actions fully implemented',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'CAPA Implemented',
        width: '130px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'criteriaEn01Gd13Completed',
      label: 'Form EN.01-ΓΔ.13 completed correctly',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'EN.01-ΓΔ.13',
        width: '120px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'criteriaVerificationDateSet',
      label: 'Verification date set and met',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Verification Date Set',
        width: '140px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'criteriaNoRecurrence',
      label: 'No recurrence during monitoring period',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'No Recurrence',
        width: '120px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'criteriaStaffTrained',
      label: 'Staff informed and/or trained on new requirements',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Staff Trained',
        width: '120px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'criteriaEvidenceInQms',
      label: 'All related evidence available in QMS',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Evidence in QMS',
        width: '120px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },

    // Evidence (read-only boilerplate; stored as true on create)
    {
      name: 'evidenceRevisedProcedures',
      label: 'Revised procedures (where applicable)',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'evidenceEn01Gd13Form',
      label: 'Completed form EN.01-ΓΔ.13',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'evidenceTrainingRecords',
      label: 'Staff information / training records',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'evidenceCapaInQms',
      label: 'CAPA records in QMS',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'evidenceInternalAuditOrMonitoring',
      label: 'Internal audit / monitoring results (where applicable)',
      type: 'hidden',
      required: false,
      defaultValue: true,
      table: false,
      filterable: false,
    },
    {
      name: 'evidenceNotes',
      label: 'Evidence Notes',
      type: 'textarea',
      required: false,
      placeholder: 'Additional evidence notes',
      rows: 2,
      table: {
        header: 'Evidence Notes',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
    },

    // Result
    {
      name: 'effectivenessResult',
      label: 'Effectiveness Result',
      type: 'select',
      required: false,
      options: EFFECTIVENESS_RESULT_OPTIONS,
      table: {
        header: 'Result',
        width: '150px',
        sortable: true,
        cellRenderer: 'statusBadge',
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'effectivenessResultNote',
      label: 'Effectiveness Result Notes',
      type: 'textarea',
      required: false,
      placeholder: 'Additional notes on effectiveness result',
      rows: 2,
      table: {
        header: 'Result Notes',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
    },
    {
      name: 'decision',
      label: 'Decision',
      type: 'select',
      required: false,
      options: DECISION_OPTIONS,
      table: {
        header: 'Decision',
        width: '140px',
        sortable: true,
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'decisionNote',
      label: 'Decision Notes',
      type: 'textarea',
      required: false,
      placeholder: 'Additional decision notes',
      rows: 2,
      table: {
        header: 'Decision Notes',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
    },

    // Sign-off
    {
      name: 'checkedAt',
      label: 'Check Date',
      type: 'date',
      required: false,
      table: {
        header: 'Check Date',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'date',
    },
    {
      name: 'checkedById',
      label: 'Checked By',
      type: 'select',
      required: false,
      placeholder: 'Select QMS user',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: (user) => user?.isQMS === true && user?.userIsActive !== false,
      options: [],
      table: {
        header: 'Checked By',
        width: '160px',
        sortable: true,
        displayField: 'checkedBy',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { integer: true },
    },
    {
      name: 'approvedAt',
      label: 'Approved Date',
      type: 'date',
      required: false,
      table: {
        header: 'Approved At',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'date',
    },
    {
      name: 'approvedById',
      label: 'Approved By',
      type: 'select',
      required: false,
      placeholder: 'Select QMS user',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: (user) => user?.isQMS === true && user?.userIsActive !== false,
      options: [],
      table: {
        header: 'Approved By',
        width: '160px',
        sortable: true,
        displayField: 'approvedBy',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { integer: true },
    },
    {
      name: 'closedAt',
      label: 'Closure Date',
      type: 'date',
      required: 'conditional',
      dependsOn: {
        field: 'statusId',
        value: isClosedStatusSelected,
      },
      table: {
        header: 'Closed At',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'date',
      validation: {},
    },
    {
      name: 'closedById',
      label: 'Closed By',
      type: 'select',
      required: 'conditional',
      placeholder: 'Select QMS user',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: (user) => user?.isQMS === true && user?.userIsActive !== false,
      options: [],
      dependsOn: {
        field: 'statusId',
        value: isClosedStatusSelected,
      },
      table: {
        header: 'Closed By',
        width: '160px',
        sortable: true,
        displayField: 'closedBy',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { integer: true },
    },
    {
      name: 'fileAttachments',
      label: 'File(s)',
      type: 'fileUpload',
      useFormFileUpload2: true,
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024,
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: {
        header: 'File(s)',
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount',
      },
      filterable: false,
      validation: { required: false },
    },
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields
  get tableColumns() {
    return this.fields
      .filter((field) => field.table !== false && field.table)
      .map((field) => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        dateFormat: field.table.dateFormat,
        originalField: field.name,
      }));
  },

  exportFileName: 'qms_capa_effectiveness_checks',

  fieldMappings: {
    status: 'status.name',
    checkedBy: 'checkedBy.userDisplayName',
    approvedBy: 'approvedBy.userDisplayName',
    closedBy: 'closedBy.userDisplayName',
  },

  formSections: [
    {
      title: 'Basic Information',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: [
        'code',
        'statusId',
        'relatedDeviationOrFinding',
        'capaDescription',
      ],
    },
    {
      title: 'Verification Method',
      icon: '🔍',
      layout: 'custom',
      customComponent: CapaEffectivenessVerificationMethod,
      fields: [],
    },
    {
      title: 'Effectiveness Criteria',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: [
        'criteriaCapaFullyImplemented',
        'criteriaEn01Gd13Completed',
        'criteriaVerificationDateSet',
        'criteriaNoRecurrence',
        'criteriaStaffTrained',
        'criteriaEvidenceInQms',
      ],
    },
    {
      title: 'Evidence Reviewed',
      icon: '📎',
      layout: 'custom',
      customComponent: CapaEffectivenessEvidenceReviewed,
      fields: [],
    },
    {
      title: 'Evidence Notes',
      icon: '📝',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['evidenceNotes'],
    },
    {
      title: 'Effectiveness Result',
      icon: '📊',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: [
        'effectivenessResult',
        'effectivenessResultNote',
        'decision',
        'decisionNote',
      ],
    },
    {
      title: 'Sign-off & Closure',
      icon: '✍️',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: [
        'checkedAt',
        'checkedById',
        'approvedAt',
        'approvedById',
        'closedAt',
        'closedById',
      ],
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments'],
    },
    {
      title: 'Reference Document',
      icon: '📄',
      layout: 'custom',
      customComponent: CapaEffectivenessReferenceDocument,
      fields: [],
    },
  ],

  trackingSection: {
    ...trackingBase,
    fields: trackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: trackingLabels.createdDateLabel,
            userEmptyText: trackingLabels.notSpecifiedText,
            dateEmptyText: trackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: trackingLabels.updatedDateLabel,
              userEmptyText: trackingLabels.notModifiedText,
              dateEmptyText: trackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  messages: {
    loading: 'Loading CAPA effectiveness checks...',
    noData:
      'No CAPA effectiveness checks found. Create your first record to get started.',
    createSuccess: 'CAPA effectiveness check created successfully!',
    updateSuccess: 'CAPA effectiveness check updated successfully!',
    deleteSuccess: 'CAPA effectiveness check deleted successfully!',
    createError: 'Failed to create CAPA effectiveness check',
    updateError: 'Failed to update CAPA effectiveness check',
    deleteError: 'Failed to delete CAPA effectiveness check',
    loadError: 'Failed to load CAPA effectiveness checks',
    validationError: 'Validation failed',
  },

  transformData: (data) => {
    const transformed = { ...data };

    delete transformed.fileAttachments;
    delete transformed.fileAttachmentCount;
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;
    delete transformed.status;
    delete transformed.checkedBy;
    delete transformed.approvedBy;
    delete transformed.closedBy;
    delete transformed.createdBy;
    delete transformed.updatedBy;

    if (transformed.referenceDocument !== undefined) {
      if (
        transformed.referenceDocument === '' ||
        transformed.referenceDocument === null
      ) {
        delete transformed.referenceDocument;
      } else {
        transformed.referenceDocument = String(
          transformed.referenceDocument,
        ).trim();
      }
    }

    const foreignKeyFields = [
      'statusId',
      'checkedById',
      'approvedById',
      'closedById',
    ];
    foreignKeyFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    const dateFields = ['checkedAt', 'approvedAt', 'closedAt'];
    dateFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const dateValue = new Date(transformed[field]);
          transformed[field] = !isNaN(dateValue.getTime()) ? dateValue : null;
        }
      }
    });

    const booleanFields = [
      'criteriaCapaFullyImplemented',
      'criteriaEn01Gd13Completed',
      'criteriaVerificationDateSet',
      'criteriaNoRecurrence',
      'criteriaStaffTrained',
      'criteriaEvidenceInQms',
    ];
    booleanFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        transformed[field] = Boolean(transformed[field]);
      }
    });

    // Verification methods & evidence are form boilerplate — always persist as documented
    [
      'methodReviewFiles',
      'methodVerifyRevisedProcedures',
      'methodEffectivenessControlsDocumented',
      'methodDueDateDefined',
      'methodStaffInformed',
      'methodQmsReview',
      'evidenceRevisedProcedures',
      'evidenceEn01Gd13Form',
      'evidenceTrainingRecords',
      'evidenceCapaInQms',
      'evidenceInternalAuditOrMonitoring',
    ].forEach((field) => {
      transformed[field] = true;
    });

    ['effectivenessResult', 'decision'].forEach((field) => {
      if (transformed[field] === '' || transformed[field] === undefined) {
        transformed[field] = null;
      }
    });

    return transformed;
  },

  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true,
  },
};

export default capaEffectivenessConfig;
